import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Screen } from "../components/Screen";
import { ConsultantCard } from "../components/ConsultantCard";
import { api } from "../api/client";
import { useAppStore } from "../state/store";
import type { Consultant, CreditCalculationResult } from "@karat/shared";

/**
 * п.20-21 ТЗ: картки консультантів + структурована заявка з повним контекстом
 * (авто/ціна/trade-in/кредитна програма/параметри розрахунку), а не просто текст.
 */
export function Consultants() {
  const [searchParams] = useSearchParams();
  const [consultants, setConsultants] = useState<Consultant[] | null>(null);
  const [formConsultantId, setFormConsultantId] = useState<string | null | "any">(null);
  const [clientName, setClientName] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [note, setNote] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const selectedCar = useAppStore((s) => s.selectedCar);
  const creditSelection = useAppStore((s) => s.creditSelection);

  useEffect(() => {
    api.get<Consultant[]>("/api/consultants").then(setConsultants).catch(() => setConsultants([]));
  }, []);

  useEffect(() => {
    if (searchParams.get("intent") === "proposal") setFormConsultantId("any");
  }, [searchParams]);

  async function submitLead(consultantId: string | null) {
    setSubmitting(true);
    try {
      let creditResult: CreditCalculationResult | null = null;
      if (selectedCar && creditSelection) {
        creditResult = await api.post<CreditCalculationResult>("/api/credit/calculate", {
          carPriceUAH: selectedCar.priceUAH,
          programId: creditSelection.programId,
          termMonths: creditSelection.termMonths,
          advancePercent: creditSelection.advancePercent,
        });
      }
      await api.post("/api/leads", {
        clientName: clientName || null,
        clientPhone: clientPhone || null,
        clientTelegramUsername: null,
        carModelName: selectedCar?.modelName ?? null,
        carTrimName: selectedCar?.trimName ?? null,
        carPriceUAH: selectedCar?.priceUAH ?? null,
        purchaseScenario: creditSelection ? "credit" : selectedCar ? "cash" : null,
        tradeInCarDescription: null,
        tradeInEstimateUAH: null,
        creditProgramName: creditResult?.programDisplayName ?? null,
        creditAdvanceUAH: creditResult?.advanceUAH ?? null,
        creditTermMonths: creditResult?.termMonths ?? null,
        creditMonthlyPaymentUAH: creditResult?.monthlyPaymentUAH ?? null,
        consultantId,
        note: note || null,
      });
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <Screen title="Заявку надіслано" showBack>
        <div className="empty-state">
          <div className="empty-state-icon">✅</div>
          Дякуємо! Консультант отримав усі деталі й зв'яжеться з вами найближчим часом.
        </div>
      </Screen>
    );
  }

  return (
    <Screen title="Консультанти" subtitle="Зв'яжіться з працівником дилерського центру" showBack>
      {formConsultantId !== null && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div className="section-title" style={{ margin: "0 0 8px" }}>
            Ваші контактні дані
          </div>
          <div className="field">
            <label>Ім'я</label>
            <input value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Як до вас звертатись" />
          </div>
          <div className="field">
            <label>Телефон</label>
            <input value={clientPhone} onChange={(e) => setClientPhone(e.target.value)} placeholder="+380" />
          </div>
          <div className="field">
            <label>Коментар (необов'язково)</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
          </div>
          <button
            className="btn btn-primary"
            disabled={!clientPhone || submitting}
            onClick={() => submitLead(formConsultantId === "any" ? null : formConsultantId)}
          >
            {submitting ? "Надсилаємо…" : "Надіслати заявку"}
          </button>
        </div>
      )}

      {consultants === null && <div className="loading-row">Завантаження…</div>}
      {consultants && (
        <div className="card-list">
          {consultants.map((c) => (
            <ConsultantCard key={c.id} consultant={c} onSendLead={() => setFormConsultantId(c.id)} />
          ))}
        </div>
      )}
    </Screen>
  );
}
